import React, { useEffect, useState, Fragment, createElement, Component } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ChevronDown, Brain, Battery, ShieldCheck, ArrowRight, Smartphone, Activity, Layers, XCircle, Zap, Clock, Target, Eye, Mail, Lock, Star, User } from 'lucide-react';
// --- Constants & Pixel Logic ---
const CHECKOUT_URL = 'https://www.metodo333.com/cart/52449087979796:1';
const META_PIXEL_ID = '1534608111621678';
declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}
const trackAddToCart = () => {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: 'Dopamina Zero Bundle',
      content_ids: ['52449087979796'],
      content_type: 'product',
      value: 6990,
      currency: 'ARS'
    });
    window.fbq('track', 'InitiateCheckout', {
      value: 6990,
      currency: 'ARS'
    });
  }
};
// --- Components ---
const FadeInSection = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.7,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <motion.a href={CHECKOUT_URL} target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} whileHover={{
  scale: 1.02,
  y: -2
}} whileTap={{
  scale: 0.98
}} className={`bg-cyan text-dark font-space font-medium text-lg px-8 py-4 rounded-lg shadow-[0_0_15px_rgba(0,212,255,0.2)] hover:shadow-[0_0_25px_rgba(0,212,255,0.4)] transition-all duration-300 flex items-center justify-center gap-3 w-full md:w-auto cursor-pointer no-underline ${className}`} data-id="element-1">
    {children}
    <ArrowRight className="w-5 h-5" data-id="element-2" />
  </motion.a>;
const SectionSpacer = () => <div className="py-12 md:py-20" data-id="element-3" />;
// --- Main App ---
export function App() {
  // Meta Pixel: Initialize and fire PageView on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.fbq) {
      window.fbq('track', 'PageView');
      window.fbq('track', 'ViewContent', {
        content_name: 'Dopamina Zero Bundle',
        content_ids: ['52449087979796'],
        content_type: 'product',
        value: 6990,
        currency: 'ARS'
      });
      return;
    }
    const f: any = window;
    const n: any = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      ;
      (document.head || document.body || document.documentElement).appendChild(script);
    }
    window.fbq?.('init', META_PIXEL_ID);
    window.fbq?.('track', 'PageView');
    window.fbq?.('track', 'ViewContent', {
      content_name: 'Dopamina Zero Bundle',
      content_ids: ['52449087979796'],
      content_type: 'product',
      value: 6990,
      currency: 'ARS'
    });
  }, []);
  // FAQ State
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const faqs = [{
    q: '¿Necesito dejar redes sociales por completo?',
    a: 'No. El objetivo no es aislarte del mundo digital, sino ayudarte a recuperar control sobre cómo lo usas y cuándo lo usas.'
  }, {
    q: '¿Cuánto tiempo necesito al día?',
    a: 'La mayoría de las personas puede aplicar el protocolo dedicando entre 15 y 30 minutos al día, además de hacer pequeños ajustes en su entorno.'
  }, {
    q: '¿Esto funciona si trabajo muchas horas con el celular o computadora?',
    a: 'Sí. Está pensado precisamente para personas que trabajan con pantallas, mensajes, notificaciones y múltiples tareas.'
  }, {
    q: '¿Es un tratamiento médico?',
    a: 'No. Es un protocolo educativo y práctico sobre enfoque, hábitos y entorno digital. No reemplaza atención médica, psicológica ni psiquiátrica.'
  }, {
    q: '¿Cuándo recibo el acceso?',
    a: 'El acceso es inmediato después del pago.'
  }, {
    q: '¿Cómo funciona la garantía?',
    a: 'Tienes 7 días para probar el protocolo. Si sientes que no te aporta claridad, estructura o más control sobre tu atención, puedes pedir un reembolso completo dentro de ese plazo.'
  }];
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-dark text-gray-300 font-inter selection:bg-cyan/20 selection:text-cyan overflow-x-hidden" data-id="element-4">
      {/* 1. HERO SECTION */}
      <section className="max-w-6xl mx-auto px-6 pt-8 md:pt-24 pb-8" data-id="element-5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-6">
          <FadeInSection className="order-1 lg:order-1" data-id="element-7">
            {/* Launch badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan/10 border border-cyan/20 text-cyan text-[11px] md:text-xs font-medium tracking-wide mb-5 md:mb-7 uppercase" data-id="element-8">
              <span aria-hidden="true" data-id="element-9">⚡</span>
              <span data-id="element-10">Precio de lanzamiento</span>
            </div>

            {/* Headline */}
            <h1 className="text-[32px] sm:text-4xl md:text-5xl lg:text-[58px] font-space font-semibold text-white leading-[1.05] mb-4 md:mb-6 tracking-tight" data-id="element-11">
              Recupera tu <span className="text-cyan" data-id="element-12">enfoque</span> en 21 días
              sin depender de fuerza de voluntad.
            </h1>

            {/* Subheadline */}
            <p className="text-base md:text-lg text-gray-400 mb-6 md:mb-8 leading-relaxed max-w-xl" data-id="element-13">
              Un sistema práctico para reducir distracciones digitales, bajar la
              niebla mental y volver a sostener trabajo profundo.
            </p>

            {/* Price block */}
            <div className="bg-dark-card border border-white/10 rounded-2xl p-5 md:p-6 mb-5 md:mb-6 max-w-md" data-id="element-14">
              <div className="flex items-baseline gap-2 mb-1.5" data-id="element-15">
                <span className="text-3xl md:text-4xl font-space font-semibold text-white tracking-tight" data-id="element-16">
                  $6.990
                </span>
                <span className="text-sm md:text-base text-gray-400 font-medium" data-id="element-17">
                  ARS — pago único
                </span>
              </div>
              <p className="text-xs md:text-sm text-gray-500" data-id="element-18">
                Acceso inmediato · Garantía de 7 días
              </p>
            </div>

            {/* Primary CTA */}
            <div className="mb-3 md:mb-4" data-id="element-19">
              <CTAButton data-id="element-20">Sí, quiero empezar los 21 días</CTAButton>
            </div>

            {/* Secondary link */}
            <button onClick={() => scrollToSection('como-funciona')} className="inline-flex items-center gap-1.5 text-cyan hover:text-cyan/80 text-sm font-medium mb-6 md:mb-8 transition-colors duration-200" data-id="element-21">
              Ver cómo funciona
              <ChevronDown className="w-4 h-4" data-id="element-22" />
            </button>

            {/* Trust badges row */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] md:text-xs text-gray-500" data-id="element-23">
              <span className="flex items-center gap-1.5" data-id="element-24">
                <CheckCircle2 className="w-3.5 h-3.5 text-cyan" data-id="element-25" /> Acceso
                inmediato
              </span>
              <span className="flex items-center gap-1.5" data-id="element-26">
                <CheckCircle2 className="w-3.5 h-3.5 text-cyan" data-id="element-27" /> Pago único
              </span>
              <span className="flex items-center gap-1.5" data-id="element-28">
                <CheckCircle2 className="w-3.5 h-3.5 text-cyan" data-id="element-29" /> Garantía 7
                días
              </span>
            </div>
          </FadeInSection>

          <FadeInSection delay={0.2} className="hidden lg:block order-2 lg:order-2 relative" data-id="element-30">
            <div className="absolute inset-0 bg-cyan/5 blur-[120px] rounded-full" data-id="element-31" />
            <img src="https://cdn.magicpatterns.com/uploads/x1U2YW7xQFvbjtoePM5hT5/heylloyd-image-1777904934763.png" alt="Dopamina Zero Protocol" loading="eager" fetchPriority="high" className="relative z-10 w-full h-auto rounded-2xl border border-white/5 shadow-2xl object-cover" data-id="element-32" />
          </FadeInSection>
        </div>
      </section>

      <SectionSpacer data-id="element-33" />

      {/* 2. PROBLEMA */}
      <section className="max-w-3xl mx-auto px-6" data-id="element-34">
        <FadeInSection data-id="element-35">
          <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-8 tracking-tight text-center" data-id="element-36">
            Tu problema no es falta de disciplina.
            <br data-id="element-37" />
            Es sobrecarga constante.
          </h2>
          <div className="text-base md:text-lg text-gray-400 leading-relaxed space-y-5 mb-10" data-id="element-38">
            <p data-id="element-39">
              Si pasas el día saltando entre WhatsApp, correo, notificaciones,
              pestañas abiertas y tareas a medias, no estás solo.
            </p>
            <p data-id="element-40">
              La dificultad para sostener atención, la sensación de niebla
              mental y el cansancio cognitivo son la consecuencia lógica de
              trabajar en un entorno lleno de interrupciones y recompensas
              instantáneas.
            </p>
            <p className="text-white font-medium" data-id="element-41">
              No necesitas exigirte más. Necesitas reducir el ruido y
              reconstruir tu capacidad de concentrarte.
            </p>
          </div>

          <ul className="grid sm:grid-cols-2 gap-3" data-id="element-42">
            {['Empiezas muchas cosas, terminas pocas', 'Cambias de contexto todo el tiempo', 'Te cuesta entrar en trabajo profundo', 'Llegas al final del día con poco avance real'].map((item, i) => <li key={i} className="flex items-start gap-3 bg-dark-card border border-white/5 px-5 py-4 rounded-xl" data-id="element-43">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan mt-2.5 shrink-0" data-id="element-44" />
                <span className="text-gray-300 text-sm" data-id="element-45">{item}</span>
              </li>)}
          </ul>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-46" />

      {/* 3. MECANISMO */}
      <section className="max-w-5xl mx-auto px-6" data-id="element-47">
        <FadeInSection data-id="element-48">
          <div className="text-center mb-12" data-id="element-49">
            <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-4 tracking-tight" data-id="element-50">
              Tu atención está siendo fragmentada todos los días
            </h2>
            <p className="text-lg text-gray-400 max-w-xl mx-auto" data-id="element-51">
              El problema no es tu capacidad. El problema es el entorno.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-10" data-id="element-52">
            {[{
            icon: <Smartphone className="w-5 h-5 text-cyan" data-id="element-53" />,
            title: 'Sobreestimulación',
            desc: 'Demasiados estímulos mantienen tu mente en alerta permanente.'
          }, {
            icon: <Layers className="w-5 h-5 text-cyan" data-id="element-54" />,
            title: 'Fragmentación atencional',
            desc: 'Cambiar de tarea cada pocos minutos destruye el enfoque sostenido.'
          }, {
            icon: <Battery className="w-5 h-5 text-cyan" data-id="element-55" />,
            title: 'Fatiga de decisión',
            desc: 'Cada microdecisión diaria consume energía mental antes de que llegues a lo importante.'
          }].map((card, i) => <div key={i} className="bg-dark-card border border-white/5 p-6 rounded-2xl hover:border-white/10 transition-colors" data-id="element-56">
                <div className="bg-dark-lighter w-10 h-10 rounded-lg flex items-center justify-center mb-4 border border-white/5" data-id="element-57">
                  {card.icon}
                </div>
                <h3 className="text-lg font-space font-medium text-white mb-2" data-id="element-58">
                  {card.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-59">
                  {card.desc}
                </p>
              </div>)}
          </div>

          <p className="text-center text-base md:text-lg text-gray-300 max-w-2xl mx-auto" data-id="element-60">
            Por eso necesitas un sistema claro para recuperar control sobre tu
            atención.
          </p>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-61" />

      {/* 4. SOLUCIÓN */}
      <section id="como-funciona" className="max-w-4xl mx-auto px-6 scroll-mt-24" data-id="element-62">
        <FadeInSection data-id="element-63">
          <div className="text-center mb-10" data-id="element-64">
            <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-6 tracking-tight" data-id="element-65">
              Qué es Dopamina Zero
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed mb-6" data-id="element-66">
              Dopamina Zero es un protocolo práctico de 21 días para ayudarte a
              reducir sobreestimulación digital, reorganizar tu entorno y volver
              a crear bloques reales de enfoque profundo.
            </p>
            <div className="flex flex-col gap-1.5 text-gray-400 text-sm md:text-base" data-id="element-67">
              <p className="flex items-center justify-center gap-2" data-id="element-68">
                <XCircle className="w-4 h-4 text-gray-500" data-id="element-69" /> No es motivación
                vacía.
              </p>
              <p className="flex items-center justify-center gap-2" data-id="element-70">
                <XCircle className="w-4 h-4 text-gray-500" data-id="element-71" /> No es teoría
                abstracta.
              </p>
              <p className="flex items-center justify-center gap-2 text-white font-medium mt-2" data-id="element-72">
                <CheckCircle2 className="w-4 h-4 text-cyan" data-id="element-73" /> Es una estructura
                simple y aplicable para volver a trabajar con claridad.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10" data-id="element-74">
            {['Identificas fugas de atención', 'Ajustas tu entorno digital', 'Recuperas bloques de trabajo profundo', 'Construyes un sistema sostenible'].map((item, i) => <div key={i} className="bg-dark-card border border-white/5 p-5 rounded-xl" data-id="element-75">
                <div className="text-cyan font-space font-medium text-sm mb-2" data-id="element-76">
                  0{i + 1}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-77">{item}</p>
              </div>)}
          </div>

          <div className="flex justify-center" data-id="element-78">
            <CTAButton data-id="element-79">Sí, quiero empezar el protocolo</CTAButton>
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-80" />

      {/* 5. QUÉ RECIBES */}
      <section className="max-w-4xl mx-auto px-6" data-id="element-81">
        <FadeInSection data-id="element-82">
          <div className="text-center mb-10" data-id="element-83">
            <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-4 tracking-tight" data-id="element-84">
              Qué recibes al entrar hoy
            </h2>
          </div>

          <div className="bg-dark-card border border-white/10 rounded-3xl p-6 md:p-10" data-id="element-85">
            <div className="space-y-6" data-id="element-86">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start" data-id="element-87">
                <div className="bg-cyan/10 p-3 rounded-xl border border-cyan/20 shrink-0" data-id="element-88">
                  <Brain className="w-7 h-7 text-cyan" data-id="element-89" />
                </div>
                <div data-id="element-90">
                  <div className="flex flex-wrap items-center gap-2 mb-2" data-id="element-91">
                    <h3 className="text-lg font-space font-medium text-white" data-id="element-92">
                      Protocolo Dopamina Zero
                    </h3>
                    <span className="text-xs font-medium bg-cyan/10 text-cyan px-2 py-0.5 rounded border border-cyan/20" data-id="element-93">
                      Producto principal
                    </span>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-94">
                    El sistema paso a paso de 21 días para reducir
                    distracciones, recuperar claridad mental y reconstruir
                    enfoque real.
                  </p>
                </div>
              </div>

              <div className="h-px w-full bg-white/5" data-id="element-95" />

              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start" data-id="element-96">
                <div className="bg-white/5 p-3 rounded-xl border border-white/10 shrink-0" data-id="element-97">
                  <Smartphone className="w-7 h-7 text-gray-400" data-id="element-98" />
                </div>
                <div data-id="element-99">
                  <h3 className="text-lg font-space font-medium text-white mb-2" data-id="element-100">
                    Bonus 1 — Configuración de entorno digital
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-101">
                    Ajustes prácticos para convertir tu celular y tu computadora
                    en herramientas de trabajo, no en fuentes constantes de
                    interrupción.
                  </p>
                </div>
              </div>

              <div className="h-px w-full bg-white/5" data-id="element-102" />

              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start" data-id="element-103">
                <div className="bg-white/5 p-3 rounded-xl border border-white/10 shrink-0" data-id="element-104">
                  <Battery className="w-7 h-7 text-gray-400" data-id="element-105" />
                </div>
                <div data-id="element-106">
                  <h3 className="text-lg font-space font-medium text-white mb-2" data-id="element-107">
                    Bonus 2 — Guía de energía y claridad
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-108">
                    Hábitos complementarios para sostener mejor rendimiento
                    mental y menos fatiga cognitiva.
                  </p>
                </div>
              </div>

              <div className="h-px w-full bg-white/5" data-id="element-109" />

              <div className="flex flex-col md:flex-row gap-4 md:gap-6 items-start" data-id="element-110">
                <div className="bg-white/5 p-3 rounded-xl border border-white/10 shrink-0" data-id="element-111">
                  <Clock className="w-7 h-7 text-gray-400" data-id="element-112" />
                </div>
                <div data-id="element-113">
                  <h3 className="text-lg font-space font-medium text-white mb-2" data-id="element-114">
                    Bonus 3 — Planner de enfoque profundo
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-115">
                    Plantillas simples para organizar prioridades, reducir
                    fatiga de decisión y proteger tu tiempo de trabajo real.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-gray-500 mt-8 pt-6 border-t border-white/5" data-id="element-116">
              <span className="flex items-center gap-1.5" data-id="element-117">
                <CheckCircle2 className="w-3.5 h-3.5 text-cyan" data-id="element-118" /> Acceso
                inmediato
              </span>
              <span className="flex items-center gap-1.5" data-id="element-119">
                <Lock className="w-3.5 h-3.5 text-cyan" data-id="element-120" /> Pago único
              </span>
              <span className="flex items-center gap-1.5" data-id="element-121">
                <ShieldCheck className="w-3.5 h-3.5 text-cyan" data-id="element-122" /> Garantía de 7
                días
              </span>
            </div>
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-123" />

      {/* 6. RESULTADOS */}
      <section className="max-w-5xl mx-auto px-6" data-id="element-124">
        <FadeInSection data-id="element-125">
          <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-10 text-center tracking-tight" data-id="element-126">
            Resultados concretos que vas a notar
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-id="element-127">
            {[{
            title: 'Te costará menos empezar tareas importantes',
            icon: <Zap className="w-4 h-4" data-id="element-128" />
          }, {
            title: 'Dejarás de cambiar de app cada pocos minutos',
            icon: <Smartphone className="w-4 h-4" data-id="element-129" />
          }, {
            title: 'Recuperarás bloques reales de trabajo sin interrupciones',
            icon: <Clock className="w-4 h-4" data-id="element-130" />
          }, {
            title: 'Llegarás al final del día con más progreso y menos agotamiento mental',
            icon: <Battery className="w-4 h-4" data-id="element-131" />
          }, {
            title: 'Tendrás más control sobre tus impulsos digitales',
            icon: <Target className="w-4 h-4" data-id="element-132" />
          }, {
            title: 'Pensarás con más claridad y menos ruido mental',
            icon: <Brain className="w-4 h-4" data-id="element-133" />
          }].map((benefit, i) => <div key={i} className="bg-dark-card border border-white/5 p-5 rounded-xl flex items-start gap-3" data-id="element-134">
                <div className="text-cyan mt-0.5 shrink-0" data-id="element-135">{benefit.icon}</div>
                <p className="text-gray-300 text-sm leading-relaxed" data-id="element-136">
                  {benefit.title}
                </p>
              </div>)}
          </div>

          <div className="text-center max-w-xl mx-auto" data-id="element-137">
            <p className="text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-138">
              No se trata solo de producir más.
              <br data-id="element-139" />
              <span className="text-white font-medium" data-id="element-140">
                Se trata de volver a sentir que tu mente trabaja contigo, no
                contra ti.
              </span>
            </p>
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-141" />

      {/* 7. TESTIMONIOS */}
      <section className="max-w-5xl mx-auto px-6" data-id="element-142">
        <FadeInSection data-id="element-143">
          <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-10 md:mb-12 text-center tracking-tight" data-id="element-144">
            Experiencias reales
          </h2>

          <div className="grid md:grid-cols-3 gap-5" data-id="element-145">
            {[{
            name: 'Carlos R.',
            role: 'Desarrollador de software',
            gender: 'male' as const,
            quote: 'Procrastinaba horas antes de sentarme a programar. Desde el día 4 del protocolo volvió a completar tareas complejas en la mañana sin pasar media jornada saltando entre distracciones.',
            result: '+3 horas de enfoque al día'
          }, {
            name: 'Mariana L.',
            role: 'Fundadora de agencia',
            gender: 'female' as const,
            quote: 'Vivía reaccionando a mensajes y urgencias todo el día. En la segunda semana recuperé calma, claridad y capacidad de estar presente en una sola tarea.',
            result: 'Menos ansiedad, más control mental'
          }, {
            name: 'Diego M.',
            role: 'Diseñador freelance',
            gender: 'male' as const,
            quote: 'Saltaba entre Figma, WhatsApp e Instagram sin ritmo real. Desde la primera semana logré sostener bloques de 90 minutos sin revisar el celular.',
            result: 'Trabajo profundo sostenido'
          }].map((test, i) => <div key={i} className="bg-[#1a1a1a] border border-white/5 p-6 rounded-2xl flex flex-col h-full hover:border-cyan/20 transition-colors duration-300" data-id="element-146">
                {/* Avatar + identity */}
                <div className="flex items-center gap-3 mb-4" data-id="element-147">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 border ${test.gender === 'female' ? 'bg-cyan/10 border-cyan/20' : 'bg-white/5 border-white/10'}`} aria-hidden="true" data-id="element-148">
                    <User className={`w-6 h-6 ${test.gender === 'female' ? 'text-cyan' : 'text-gray-300'}`} data-id="element-149" />
                  </div>
                  <div data-id="element-150">
                    <h4 className="text-white font-space font-medium text-sm leading-tight" data-id="element-151">
                      {test.name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-152">{test.role}</p>
                  </div>
                </div>

                {/* Stars */}
                <div className="flex items-center gap-0.5 mb-4" aria-label="5 de 5 estrellas" data-id="element-153">
                  {[0, 1, 2, 3, 4].map(s => <Star key={s} className="w-4 h-4 text-yellow-400 fill-yellow-400" data-id="element-154" />)}
                </div>

                {/* Quote */}
                <p className="text-gray-300 text-sm leading-relaxed mb-5 flex-grow" data-id="element-155">
                  “{test.quote}”
                </p>

                {/* Result badge */}
                <div className="inline-flex items-center gap-1.5 self-start px-3 py-1.5 rounded-full bg-cyan/10 border border-cyan/20" data-id="element-156">
                  <CheckCircle2 className="w-3.5 h-3.5 text-cyan shrink-0" data-id="element-157" />
                  <span className="text-cyan text-xs font-medium" data-id="element-158">
                    {test.result}
                  </span>
                </div>
              </div>)}
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-159" />

      {/* 8. FAQ */}
      <section className="max-w-3xl mx-auto px-6" data-id="element-160">
        <FadeInSection data-id="element-161">
          <h2 className="text-3xl font-space font-medium text-white mb-8 text-center tracking-tight" data-id="element-162">
            Preguntas frecuentes
          </h2>
          <div className="space-y-3" data-id="element-163">
            {faqs.map((faq, index) => <div key={index} className="bg-dark-card border border-white/5 rounded-xl overflow-hidden transition-all duration-300" data-id="element-164">
                <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full px-5 md:px-6 py-4 text-left flex items-center justify-between focus:outline-none gap-4" data-id="element-165">
                  <span className="font-space font-medium text-white text-base md:text-lg" data-id="element-166">
                    {faq.q}
                  </span>
                  <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform duration-300 shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} data-id="element-167" />
                </button>
                <div className={`px-5 md:px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === index ? 'max-h-60 pb-5 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-168">
                  <p className="text-gray-400 leading-relaxed text-sm md:text-base" data-id="element-169">
                    {faq.a}
                  </p>
                </div>
              </div>)}
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-170" />

      {/* 9. GARANTÍA */}
      <section className="max-w-3xl mx-auto px-6" data-id="element-171">
        <FadeInSection data-id="element-172">
          <div className="bg-dark-card border border-white/10 rounded-2xl p-8 md:p-10 text-center" data-id="element-173">
            <ShieldCheck className="w-10 h-10 text-white/80 mx-auto mb-5" data-id="element-174" />
            <h2 className="text-2xl font-space font-medium text-white mb-3" data-id="element-175">
              Garantía de 7 días
            </h2>
            <p className="text-gray-400 leading-relaxed max-w-xl mx-auto text-sm md:text-base" data-id="element-176">
              Pruébalo durante 7 días. Si sientes que el protocolo no te aporta
              claridad, estructura o control sobre tu atención, puedes pedir un
              reembolso completo dentro de ese plazo.
            </p>
          </div>
        </FadeInSection>
      </section>

      <SectionSpacer data-id="element-177" />

      {/* 10. CIERRE / PRECIO / CTA FINAL */}
      <section className="max-w-3xl mx-auto px-6 pb-20" data-id="element-178">
        <FadeInSection data-id="element-179">
          <div className="bg-dark-card border border-white/10 rounded-3xl p-6 md:p-12 text-center relative overflow-hidden" data-id="element-180">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan/50 to-transparent" data-id="element-181" />

            <h2 className="text-3xl md:text-4xl font-space font-medium text-white mb-4 tracking-tight" data-id="element-182">
              Empieza hoy a recuperar tu enfoque
            </h2>

            <p className="text-gray-400 leading-relaxed max-w-xl mx-auto mb-8 text-sm md:text-base" data-id="element-183">
              Si sientes que tu atención está dispersa, tu energía mental se
              drena demasiado rápido y te cuesta entrar en trabajo profundo,
              este protocolo te da una estructura práctica para volver a tomar
              control.
            </p>

            <div className="flex justify-center items-end gap-2 mb-6" data-id="element-184">
              <span className="text-5xl md:text-6xl font-space font-medium text-white" data-id="element-185">
                $6.990
              </span>
              <span className="text-xl text-gray-500 font-medium mb-2" data-id="element-186">
                ARS
              </span>
            </div>

            <div className="bg-dark-lighter border border-white/5 rounded-xl p-5 mb-6 text-left max-w-md mx-auto" data-id="element-187">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3 text-center" data-id="element-188">
                Incluye
              </p>
              <ul className="space-y-2.5" data-id="element-189">
                {['Protocolo Dopamina Zero', 'Bonus 1: Configuración de entorno digital', 'Bonus 2: Guía de energía y claridad', 'Bonus 3: Planner de enfoque profundo'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 text-sm" data-id="element-190">
                    <CheckCircle2 className="w-4 h-4 text-cyan shrink-0 mt-0.5" data-id="element-191" />
                    <span data-id="element-192">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-gray-400 mb-8" data-id="element-193">
              <span className="flex items-center gap-1.5" data-id="element-194">
                <Lock className="w-3.5 h-3.5 text-cyan" data-id="element-195" /> Pago único
              </span>
              <span className="flex items-center gap-1.5" data-id="element-196">
                <Clock className="w-3.5 h-3.5 text-cyan" data-id="element-197" /> Acceso inmediato
              </span>
              <span className="flex items-center gap-1.5" data-id="element-198">
                <ShieldCheck className="w-3.5 h-3.5 text-cyan" data-id="element-199" /> Garantía de 7
                días
              </span>
            </div>

            <CTAButton className="mx-auto py-5 px-10 text-lg md:text-xl" data-id="element-200">
              Sí, quiero recuperar mi enfoque
            </CTAButton>
          </div>
        </FadeInSection>
      </section>

      {/* 11. FOOTER */}
      <footer className="border-t border-white/5 bg-dark-card py-10" data-id="element-201">
        <div className="max-w-6xl mx-auto px-6 flex flex-col gap-6" data-id="element-202">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-5" data-id="element-203">
            <div className="text-gray-500 text-sm" data-id="element-204">
              <p className="text-white font-space font-medium mb-1" data-id="element-205">
                Dopamina Zero
              </p>
              <p className="text-xs" data-id="element-206">© 2026 Todos los derechos reservados.</p>
            </div>
          </div>
          <div className="border-t border-white/5 pt-5" data-id="element-207">
            <p className="text-xs text-gray-500 leading-relaxed max-w-3xl" data-id="element-208">
              Este producto tiene fines educativos e informativos. No reemplaza
              atención médica, psicológica ni psiquiátrica profesional.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}